import { fabric } from 'fabric';
import {
  createContext,
  MutableRefObject,
  useCallback,
  PropsWithChildren,
  useState,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import useUndo, {
  State as HistoryState,
} from '../../../../common/editor-hooks/useUndo';
import { SelectEvent, SelectMode } from '../../../types/types';
import { releaseVideo } from '../utils/handleVideo';
import type { ClipboardEntry } from '../utils/clipboard';

const DEFAULT_TAB = 'Text';
const RULERS_SETTING_KEY = 'canvasEditor.rulers';

const readRulersSetting = () => {
  try {
    return localStorage.getItem(RULERS_SETTING_KEY) !== 'off';
  } catch {
    return true;
  }
};
const DEFAULT_COLOR = 'white';
const HISTORY_LIMIT = 50;

type State = {
  canvas: fabric.Canvas | null;
  setCanvas: (canvas: fabric.Canvas) => void;
  activeTab: string;
  setActiveTab: (activeTab: string) => void;
  activeColor: any;
  setActiveColor: (activeTab: any) => void;
  setHistory: (history: any) => void;
  resetHistory: (history: any) => void;
  initLoad: boolean;
  setInitLoad: (init: boolean) => void;
  copiedObjects: ClipboardEntry[];
  setCopiedObjects: (copiedObjects: any) => void;
  element: string;
  setElement: (elem: string) => void;
  selectMode: SelectMode | '';
  searchAsset: string;
  setSearchAsset: (search: string) => void;
  // Requested zoom; null fits the slide to the workspace.
  zoomScale: number | null;
  setZoomScale: (scale: number | null) => void;
  // Zoom the canvas currently has (also when fitted).
  appliedZoom: number;
  setAppliedZoom: (zoom: number) => void;
  // Screen point to keep in place when the next zoom is applied.
  zoomAnchorRef: MutableRefObject<ZoomAnchor | null>;
  showRulers: boolean;
  setShowRulers: (show: boolean) => void;
};

export interface ZoomAnchor {
  clientX: number;
  clientY: number;
}

// Undo history changes on every edit, so it has its own context: only the
// few components that read it re-render per edit, not every editor consumer.
type HistoryContextState = {
  history: HistoryState<fabric.Object[][] | object>;
  undo: () => void;
  redo: () => void;
  canUndo: boolean;
  canRedo: boolean;
};

const Context = createContext<State | null>(null);
const HistoryContext = createContext<HistoryContextState | null>(null);

function EditorProvider({ children }: PropsWithChildren) {
  const [canvas, setCanvas] = useState<State['canvas']>(null);
  const [activeTab, setActiveTab] = useState<State['activeTab']>(DEFAULT_TAB);
  const [activeColor, setActiveColor] =
    useState<State['activeColor']>(DEFAULT_COLOR);
  const [
    history,
    { set: setHistory, reset: resetHistory, undo, redo, canUndo, canRedo },
  ] = useUndo<any>([], { limit: HISTORY_LIMIT });
  const [initLoad, setInitLoad] = useState<boolean>(false);
  const [copiedObjects, setCopiedObjects] = useState<ClipboardEntry[]>([]);
  const [element, setElement] = useState('');
  const [selectMode, setSelectMode] = useState<SelectMode | ''>('');
  const [searchAsset, setSearchAsset] = useState('');
  const [zoomScale, setZoomScale] = useState<number | null>(null);
  const [appliedZoom, setAppliedZoom] = useState(1);
  const zoomAnchorRef = useRef<ZoomAnchor | null>(null);
  const [showRulers, setShowRulersState] = useState(readRulersSetting);
  const setShowRulers = useCallback((show: boolean) => {
    setShowRulersState(show);
    try {
      localStorage.setItem(RULERS_SETTING_KEY, show ? 'on' : 'off');
    } catch {
      // Not remembered; the setting still applies to this session.
    }
  }, []);
  const trackedVideos = useRef(new Set<fabric.Object>());

  const listener = (e: Event) => {
    const selectEvent = e as CustomEvent;
    setSelectMode(selectEvent.detail);
  };

  useEffect(() => {
    document.addEventListener(SelectEvent.ONE, listener);
    return () => document.removeEventListener(SelectEvent.ONE, listener);
  }, []);

  // History entries keep live objects so undo can restore them. Once a video
  // is neither on the canvas nor in any entry it can never come back, so its
  // media is released.
  useEffect(() => {
    if (!canvas) return;
    const referenced = new Set<fabric.Object>(canvas.getObjects());
    for (const entry of [...history.past, history.present, ...history.future]) {
      if (Array.isArray(entry)) {
        entry.forEach(({ object }) => object && referenced.add(object));
      }
    }
    referenced.forEach((object) => {
      if (object.data?.type === 'video') trackedVideos.current.add(object);
    });
    trackedVideos.current.forEach((object) => {
      if (!referenced.has(object)) {
        releaseVideo(object);
        trackedVideos.current.delete(object);
      }
    });
  }, [history, canvas]);

  // Selection listeners, centralized here so they run exactly once per editor
  // session no matter how many components read the canvas via
  // useFabricEditor() (see useFabricEditor.tsx). Rendering is on demand;
  // playing videos drive their own loop (startVideoRenderLoop).
  useEffect(() => {
    if (!canvas) return;

    const handleSelectionCleared = () => {
      const { backgroundColor } = canvas;
      if (backgroundColor) setActiveColor(backgroundColor);
    };
    // Also when the selection moves straight to another object.
    const handleSelection = () => {
      const fill = canvas.getActiveObjects()[0]?.fill;
      if (fill) setActiveColor(fill);
    };
    canvas.on('selection:cleared', handleSelectionCleared);
    canvas.on('selection:created', handleSelection);
    canvas.on('selection:updated', handleSelection);

    return () => {
      canvas.off('selection:cleared', handleSelectionCleared);
      canvas.off('selection:created', handleSelection);
      canvas.off('selection:updated', handleSelection);
    };
  }, [canvas]);

  const value = useMemo(
    () => ({
      canvas,
      setCanvas,
      activeTab,
      setActiveTab,
      activeColor,
      setActiveColor,
      setHistory,
      resetHistory,
      initLoad,
      setInitLoad,
      copiedObjects,
      setCopiedObjects,
      element,
      setElement,
      selectMode,
      searchAsset,
      setSearchAsset,
      zoomScale,
      setZoomScale,
      appliedZoom,
      setAppliedZoom,
      zoomAnchorRef,
      showRulers,
      setShowRulers,
    }),
    [
      canvas,
      activeTab,
      activeColor,
      setHistory,
      resetHistory,
      initLoad,
      copiedObjects,
      element,
      selectMode,
      searchAsset,
      zoomScale,
      appliedZoom,
      showRulers,
      setShowRulers,
    ],
  );

  const historyValue = useMemo(
    () => ({ history, undo, redo, canUndo, canRedo }),
    [history, undo, redo, canUndo, canRedo],
  );

  return (
    <Context.Provider value={value}>
      <HistoryContext.Provider value={historyValue}>
        {children}
      </HistoryContext.Provider>
    </Context.Provider>
  );
}

function useEditorData() {
  const context = useContext(Context);
  if (!context) {
    throw new Error('useEditorData must be used within a EditorProvider');
  }
  return context;
}

function useEditorHistory() {
  const context = useContext(HistoryContext);
  if (!context) {
    throw new Error('useEditorHistory must be used within a EditorProvider');
  }
  return context;
}

export { EditorProvider, useEditorData, useEditorHistory };
